/**
 * Sons « jeu » de La Régie (SPEC §9) — 100 % programmatiques (WebAudio), zéro
 * asset lourd, zéro dépendance. Cinq sons TRÈS discrets :
 *   - confirm   : pop doux (une décision confirmée)
 *   - reject    : thud grave (une décision refusée)
 *   - newCard   : tick léger (une nouvelle carte arrive en tête)
 *   - milestone : carillon bref (palier de session tous les 10 traitements)
 *   - win       : arpège bref montant (portefeuille — mission A2 « AMENDEMENTS
 *                 07-10 soir » : une vente attribuée au chatteur vient de se
 *                 payer, portefeuille). Distinct de `milestone`
 *                 (3 notes plus vives, montant réel derrière) — même discipline
 *                 de discrétion (peak ≤ 0.08).
 *
 * Le pattern audio existant du cockpit (`usePaymentSound`) lit des fichiers WAV
 * de notification de PAIEMENT — inadapté ici (paliers d'argent, ~3 Mo d'assets).
 * Pour 4 bips d'UI on synthétise à la volée : aucun fichier à charger, volume
 * bas, coupables à tout moment.
 *
 * Mute : toggle PERSISTANT (localStorage `regie:muted`), visible dans le rail.
 * Autoplay : l'AudioContext est créé/repris paresseusement au 1er son — dans La
 * Régie le superviseur a toujours déjà interagi (clic/raccourci) avant qu'un son
 * ne parte, donc le contexte est débloqué.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export type RegieSoundKind = "confirm" | "reject" | "newCard" | "milestone" | "win";

const MUTE_STORAGE_KEY = "regie:muted";

/** Lecture SYNCHRONE du flag mute persistant (défaut : son actif = opt-out). */
function readMutedPref(): boolean {
  try {
    return localStorage.getItem(MUTE_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeMutedPref(muted: boolean): void {
  try {
    localStorage.setItem(MUTE_STORAGE_KEY, muted ? "1" : "0");
  } catch {
    /* localStorage indisponible (mode privé / quota) — on ignore. */
  }
}

/** Un bip = une enveloppe de gain sur un oscillateur, éventuellement glissant. */
interface Blip {
  type: OscillatorType;
  /** Fréquence de départ (Hz). */
  from: number;
  /** Fréquence d'arrivée (Hz) — glissando linéaire jusqu'à `durationMs`. */
  to: number;
  durationMs: number;
  /** Gain crête (0..1). Volontairement bas (≤ 0.08) — sons d'UI discrets. */
  peak: number;
  /** Décalage de départ (ms) — pour enchaîner deux notes (carillon). */
  delayMs?: number;
}

const RECIPES: Record<RegieSoundKind, Blip[]> = {
  // Pop doux montant.
  confirm: [{ type: "sine", from: 620, to: 880, durationMs: 110, peak: 0.06 }],
  // Thud grave descendant.
  reject: [{ type: "triangle", from: 200, to: 120, durationMs: 150, peak: 0.07 }],
  // Tick très bref et léger.
  newCard: [{ type: "sine", from: 1180, to: 1180, durationMs: 45, peak: 0.03 }],
  // Carillon : deux notes ascendantes.
  milestone: [
    { type: "sine", from: 784, to: 784, durationMs: 130, peak: 0.05 },
    { type: "sine", from: 1046, to: 1046, durationMs: 200, peak: 0.05, delayMs: 110 },
  ],
  // Arpège trois notes, vif et montant — « ça vient de payer ».
  win: [
    { type: "sine", from: 659, to: 659, durationMs: 90, peak: 0.06 },
    { type: "sine", from: 831, to: 831, durationMs: 90, peak: 0.06, delayMs: 80 },
    { type: "sine", from: 1109, to: 1109, durationMs: 220, peak: 0.07, delayMs: 160 },
  ],
};

type AudioCtor = typeof AudioContext;

function getAudioContextCtor(): AudioCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    AudioContext?: AudioCtor;
    webkitAudioContext?: AudioCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

/**
 * Hook de sons de La Régie. Renvoie `play(kind)` (no-op si mute ou WebAudio
 * indisponible) et l'état/contrôle du mute persistant.
 */
export function useRegieSounds(): {
  muted: boolean;
  toggleMute: () => void;
  play: (kind: RegieSoundKind) => void;
} {
  const [muted, setMuted] = useState<boolean>(() => readMutedPref());
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const ctxRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    return () => {
      // Ferme le contexte au démontage (libère le thread audio).
      const ctx = ctxRef.current;
      ctxRef.current = null;
      if (ctx) void ctx.close().catch(() => {});
    };
  }, []);

  const toggleMute = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      writeMutedPref(next);
      return next;
    });
  }, []);

  const play = useCallback((kind: RegieSoundKind) => {
    if (mutedRef.current) return;
    const Ctor = getAudioContextCtor();
    if (!Ctor) return;
    let ctx = ctxRef.current;
    if (!ctx) {
      try {
        ctx = new Ctor();
      } catch {
        return;
      }
      ctxRef.current = ctx;
    }
    // Le contexte peut être suspendu (politique autoplay) — on tente de le
    // reprendre ; l'appel part depuis un geste utilisateur (clic/raccourci).
    if (ctx.state === "suspended") void ctx.resume().catch(() => {});

    const now = ctx.currentTime;
    for (const blip of RECIPES[kind]) {
      const start = now + (blip.delayMs ?? 0) / 1000;
      const end = start + blip.durationMs / 1000;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = blip.type;
      osc.frequency.setValueAtTime(blip.from, start);
      if (blip.to !== blip.from) {
        osc.frequency.linearRampToValueAtTime(blip.to, end);
      }
      // Enveloppe : attaque courte puis extinction douce (pas de clic).
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(blip.peak, start + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, end);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(end + 0.02);
    }
  }, []);

  return { muted, toggleMute, play };
}
